import { FileIcon, FileTextIcon, FileSpreadsheetIcon, FileCodeIcon, FileArchiveIcon, ImageIcon, Music, Video as VideoIcon, X } from "lucide-react";
import { File } from "@/types/breeze/File";
import { useState } from "react";
import ImageGalleryOverlay from "@/components/ui/ImageGalleryOverlay";
import { useSecureImage } from "@/hooks/useSecureFile";
import { downloadSecureFile, resolveMediaCategory } from "@/utils/fileUtils";
import { Box, Flex, Text, Grid, IconButton } from "@radix-ui/themes";

interface FileDisplayProps {
  files: File[];
  onRemove?: (index: number) => void;
  disabled?: boolean;
  enableGallery?: boolean;
  /** Indicates if this component is being rendered inside an ImageCarousel */
  insideCarousel?: boolean;
}

function SecureVideoPreview({ file }: { file: File }) {
  const { blobUrl, loading } = useSecureImage(file.id, true);
  const [failed, setFailed] = useState(false);

  if (loading) {
    return (
      <Flex align="center" justify="center" style={{ width: "100%", height: "100%", backgroundColor: "var(--gray-3)" }}>
        <Box style={{ width: 24, height: 24, border: "2px solid var(--blue-9)", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </Flex>
    );
  }
  if (!blobUrl || failed) {
    return <FileIconFallback file={file} />;
  }
  return (
    <video
      src={blobUrl}
      preload="metadata"
      muted
      playsInline
      onError={() => setFailed(true)}
      style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "black" }}
    />
  );
}

function FileIconFallback({ file }: { file: File }) {
  const { Icon, color } = getFileTypeIcon(file.file_type, file.original_file_name);
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "var(--gray-3)",
        padding: 12,
      }}
    >
      <Icon style={{ width: 32, height: 32, color }} />
      <Text size="1" color="gray" style={{ textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
        {file.original_file_name}
      </Text>
    </Flex>
  );
}

// Component for secure image display
function SecureFileImage({
  file,
  onClick,
}: {
  file: File;
  onClick?: (e: React.MouseEvent) => void;
}) {
  // Use CDN URL directly for public files — no auth round-trip needed
  const isPublic = file.is_public && file.cdn_url;
  const { blobUrl, loading } = useSecureImage(
    isPublic ? null : file.id,
    !isPublic,
  );
  const [imgFailed, setImgFailed] = useState(false);

  const imageUrl = isPublic ? file.cdn_url : blobUrl;

  if (!isPublic && loading) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "var(--gray-3)",
        }}
      >
        <Box
          style={{
            width: "24px",
            height: "24px",
            border: "2px solid var(--blue-9)",
            borderTop: "2px solid transparent",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      </Flex>
    );
  }

  if (!imageUrl || imgFailed) {
    return <FileIconFallback file={file} />;
  }

  return (
    <Box
      style={{ position: "relative", width: "100%", height: "100%" }}
      onClick={onClick}
    >
      <img
        src={imageUrl}
        alt={file.original_file_name}
        onError={() => setImgFailed(true)}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </Box>
  );
}

export function getFileTypeIcon(mimeType: string, fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (mimeType.startsWith("image/")) return { Icon: ImageIcon, color: "var(--green-9)" };
  if (mimeType.startsWith("audio/")) return { Icon: Music, color: "var(--purple-9)" };
  if (mimeType.startsWith("video/")) return { Icon: VideoIcon, color: "var(--blue-9)" };
  if (mimeType.includes("pdf") || ext === "pdf") return { Icon: FileTextIcon, color: "var(--red-9)" };
  if (mimeType.includes("spreadsheet") || ["xlsx", "xls", "csv"].includes(ext)) return { Icon: FileSpreadsheetIcon, color: "var(--green-9)" };
  if (mimeType.includes("document") || ["doc", "docx", "rtf"].includes(ext)) return { Icon: FileTextIcon, color: "var(--blue-9)" };
  if (mimeType.includes("json") || mimeType.includes("xml") || mimeType.includes("javascript") || ["js", "ts", "json", "xml", "html", "css", "py", "cs"].includes(ext)) return { Icon: FileCodeIcon, color: "var(--violet-9)" };
  if (mimeType.includes("zip") || mimeType.includes("archive") || mimeType.includes("compressed") || ["zip", "rar", "7z", "tar", "gz"].includes(ext)) return { Icon: FileArchiveIcon, color: "var(--amber-9)" };
  if (mimeType.startsWith("text/")) return { Icon: FileTextIcon, color: "var(--gray-9)" };
  return { Icon: FileIcon, color: "var(--gray-9)" };
}

export default function FileDisplay({
  files,
  onRemove,
  disabled = false,
  enableGallery = true,
  insideCarousel = false,
}: Readonly<FileDisplayProps>) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // If we're inside a carousel, we should not open our own gallery
  const shouldEnableGallery = enableGallery && !insideCarousel;

  if (!files || files.length === 0) return <Text>No files</Text>;

  const isImageFile = (file: File) => {
    if (file.file_type.startsWith("image/")) return true;
    const ext = file.original_file_name.split('.').pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"].includes(ext || "");
  };

  const imageFiles = files.filter(isImageFile);
  const hasImages = imageFiles.length > 0;

  const openGallery = (index: number) => {
    if (!shouldEnableGallery) return;
    setSelectedImageIndex(index);
    setGalleryOpen(true);
  };

  const gridColumns =
    files.length === 1
      ? { initial: "1" }
      : { initial: "2", sm: "3", md: "4", lg: "6", xl: "8" };

  return (
    <>
      <Grid
        columns={gridColumns}
        gap="3"
        style={files.length === 1 ? { maxWidth: "100%" } : {}}
      >
        {files.map((file, index) => {
          const isImage = isImageFile(file);
          const isVideo = resolveMediaCategory(file) === "video";

          // For images, we either open the gallery or navigate to the URL directly
          const handleImageClick = (e: React.MouseEvent) => {
            if (isImage && enableGallery) {
              e.preventDefault();
              const imageIndex = imageFiles.findIndex(
                (img) => img.id === file.id,
              );
              if (imageIndex !== -1) {
                openGallery(imageIndex);
              }
            }
          };

          // For non-images, we still need to handle secure downloads
          const handleFileClick = async (e: React.MouseEvent) => {
            if (!isImage) {
              e.preventDefault();
              try {
                await downloadSecureFile(file.id, file.original_file_name);
              } catch (error) {
                console.error("Failed to download file:", error);
              }
            }
          };

          const fileHeight = files.length === 1 ? "128px" : "96px";

          return (
            <Box
              key={`file-${file.id}`}
              style={{
                backgroundColor: isImage ? "transparent" : "var(--gray-2)",
                borderRadius: "var(--radius-3)",
                overflow: "hidden",
                cursor: "pointer",
                position: "relative",
                width: "100%",
                height: fileHeight,
                transition: "all 0.2s",
                border: isImage ? "none" : "1px solid var(--gray-5)",
              }}
              onClick={isImage ? handleImageClick : handleFileClick}
              data-file-container={isImage ? "image" : "file"}
            >
              {isImage ? (
                <SecureFileImage file={file} />
              ) : isVideo ? (
                <SecureVideoPreview file={file} />
              ) : (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  style={{
                    height: "100%",
                    padding: "12px",
                    gap: "4px",
                  }}
                >
                  {(() => {
                    const { Icon, color } = getFileTypeIcon(file.file_type, file.original_file_name);
                    return <Icon size={24} color={color} style={{ opacity: 0.8 }} />;
                  })()}
                  <Box style={{ textAlign: "center", minWidth: 0 }}>
                    <Text
                      size="1"
                      weight="medium"
                      style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {file.original_file_name}
                    </Text>
                    <Text size="1" color="gray">
                      {(file.file_size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </Box>
                </Flex>
              )}

              {onRemove && !disabled && (
                <Box
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 1,
                  }}
                >
                  <IconButton
                    variant="solid"
                    color="gray"
                    size="1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(index);
                    }}
                    style={{
                      backgroundColor: "white",
                      color: "var(--gray-9)",
                      borderRadius: "9999px",
                      boxShadow: "var(--shadow-2)",
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                    data-remove-button="true"
                    aria-label={`Remove ${file.original_file_name}`}
                  >
                    <X size={16} />
                  </IconButton>
                </Box>
              )}
            </Box>
          );
        })}
      </Grid>

      {/* Image Gallery Overlay */}
      {galleryOpen && hasImages && (
        <ImageGalleryOverlay
          files={imageFiles}
          initialIndex={selectedImageIndex}
          onClose={() => setGalleryOpen(false)}
        />
      )}

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        [data-file-container="file"]:hover {
          border-color: var(--blue-7) !important;
          box-shadow: 0 0 0 2px var(--blue-7);
        }
        [data-file-container="image"]:hover {
          opacity: 0.9;
        }
        [data-file-container]:hover [data-remove-button="true"] {
          opacity: 1 !important;
        }
      `}</style>
    </>
  );
}
